import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { MagnifyingGlass, FunnelSimple, Clock, Eye, ArrowLeft, ArrowRight, Tag, X, CaretDown,
  ShoppingCart, BookOpenText, Knife, Fire } from '@phosphor-icons/react';
import { useCart } from '../../context/CartContext';
import { MagereHeader, MagereFooter } from './MagereSharedLayout';

const API = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_STORE_SLUG = "magere-viandes";

const StoreBlogPage = ({ storeSlug: propStoreSlug }) => {
  const { storeSlug: paramStoreSlug } = useParams();
  const storeSlug = propStoreSlug || paramStoreSlug || DEFAULT_STORE_SLUG;
  const standalone = !!propStoreSlug || !paramStoreSlug;
  const [searchParams, setSearchParams] = useSearchParams();
  const [store, setStore] = useState(null);
  const [pages, setPages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [data, setData] = useState({ posts: [], total: 0, pages: 0, filters: {} });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [activeCategory, setActiveCategory] = useState(searchParams.get('category') || '');
  const [activeMeat, setActiveMeat] = useState(searchParams.get('meat_type') || '');
  const [activeDish, setActiveDish] = useState(searchParams.get('dish_type') || '');
  const [search, setSearch] = useState('');
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [sidebarSections, setSidebarSections] = useState({ categories: true, meats: true, dishes: false });
  const { itemCount, setIsOpen, setStoreSlug } = useCart();

  // Helper for standalone links
  const getLink = (path) => standalone ? path : `/store/${storeSlug}${path}`;

  useEffect(() => { if (storeSlug) setStoreSlug(storeSlug); }, [storeSlug, setStoreSlug]);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/s/${storeSlug}`).then(r => r.json()),
      fetch(`${API}/api/s/${storeSlug}/pages`).then(r => r.json()).catch(() => []),
      fetch(`${API}/api/s/${storeSlug}/categories`).then(r => r.json()).catch(() => []),
    ]).then(([s, p, c]) => { setStore(s); setPages(p || []); setCategories(c || []); }).catch(() => {});
  }, [storeSlug]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set('page', page);
    params.set('limit', '12');
    if (activeCategory) params.set('category', activeCategory);
    if (activeMeat) params.set('meat_type', activeMeat);
    if (activeDish) params.set('dish_type', activeDish);
    fetch(`${API}/api/s/${storeSlug}/blog?${params}`)
      .then(r => r.json()).then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [storeSlug, page, activeCategory, activeMeat, activeDish]);

  const colors = store?.theme_config?.colors || { primary: '#c2185b', secondary: '#4a1942', accent: '#ffc107' };
  const fontFamily = "'Jost', sans-serif";

  const filterLabels = {
    categories: { recettes: 'Recettes', cuisson: 'Cuisson', conseils: 'Conseils', conservation: 'Conservation' },
    meat_types: { boeuf: 'Boeuf', agneau: 'Agneau', poulet: 'Poulet', porc: 'Porc', veau: 'Veau', gibier: 'Gibier' },
    dish_types: { grillades: 'Grillades', barbecue: 'Barbecue', roti: 'Roti', mijote: 'Mijoté', poele: 'Poêle', gastronomique: 'Gastronomique', condiment: 'Condiment' }
  };

  const catIcons = {
    recettes: <Knife size={16} weight="fill" />,
    cuisson: <Fire size={16} weight="fill" />,
    conseils: <BookOpenText size={16} weight="fill" />,
    conservation: <Tag size={16} weight="fill" />
  };

  const resetFilters = () => { setActiveCategory(''); setActiveMeat(''); setActiveDish(''); setPage(1); };
  const hasFilters = activeCategory || activeMeat || activeDish;

  const filteredPosts = search
    ? data.posts.filter(p => p.title.toLowerCase().includes(search.toLowerCase()) || p.excerpt?.toLowerCase().includes(search.toLowerCase()))
    : data.posts;

  const isRecipe = (post) => post.category === 'recettes';

  useEffect(() => {
    if (!store) return;
    document.title = `Nos conseils - ${store?.identity?.company_name || store?.name || 'Blog'}`;
  }, [store]);

  /* ═══════ SIDEBAR FILTER ═══════ */
  const SidebarFilter = ({ className = '' }) => (
    <div className={className} data-testid="blog-sidebar">
      {/* Active filters */}
      {hasFilters && (
        <div className="mb-5 p-3 rounded-xl bg-slate-50 border border-slate-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Filtres actifs</span>
            <button onClick={resetFilters} className="text-[11px] underline hover:no-underline" style={{ color: colors.primary }} data-testid="reset-filters">Tout effacer</button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {activeCategory && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold text-white" style={{ backgroundColor: colors.primary }}>
                {filterLabels.categories[activeCategory] || activeCategory}
                <X size={10} weight="bold" className="cursor-pointer" onClick={() => { setActiveCategory(''); setPage(1); }} />
              </span>
            )}
            {activeMeat && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold text-white bg-amber-500">
                {filterLabels.meat_types[activeMeat] || activeMeat}
                <X size={10} weight="bold" className="cursor-pointer" onClick={() => { setActiveMeat(''); setPage(1); }} />
              </span>
            )}
            {activeDish && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold text-white bg-emerald-600">
                {filterLabels.dish_types[activeDish] || activeDish}
                <X size={10} weight="bold" className="cursor-pointer" onClick={() => { setActiveDish(''); setPage(1); }} />
              </span>
            )}
          </div>
        </div>
      )}

      {/* Category section */}
      <div className="mb-4">
        <button onClick={() => setSidebarSections(s => ({ ...s, categories: !s.categories }))}
          className="flex items-center justify-between w-full py-2 text-left" data-testid="sidebar-toggle-categories">
          <span className="text-[13px] font-bold uppercase tracking-wider text-slate-700">Type de contenu</span>
          <CaretDown size={14} className={`text-slate-400 transition-transform ${sidebarSections.categories ? 'rotate-180' : ''}`} />
        </button>
        {sidebarSections.categories && (
          <div className="space-y-0.5 mt-1">
            {(data.filters?.categories || []).map(cat => (
              <button key={cat} onClick={() => { setActiveCategory(activeCategory === cat ? '' : cat); setPage(1); }}
                className={`flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-[13px] font-medium transition-all ${activeCategory === cat ? 'text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                style={activeCategory === cat ? { backgroundColor: colors.primary } : {}}
                data-testid={`filter-cat-${cat}`}>
                <span className={activeCategory === cat ? 'text-white' : ''} style={activeCategory !== cat ? { color: colors.primary } : {}}>{catIcons[cat] || <Tag size={16} />}</span>
                {filterLabels.categories[cat] || cat}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-slate-100 my-3" />

      {/* Meat types */}
      <div className="mb-4">
        <button onClick={() => setSidebarSections(s => ({ ...s, meats: !s.meats }))}
          className="flex items-center justify-between w-full py-2 text-left" data-testid="sidebar-toggle-meats">
          <span className="text-[13px] font-bold uppercase tracking-wider text-slate-700">Type de viande</span>
          <CaretDown size={14} className={`text-slate-400 transition-transform ${sidebarSections.meats ? 'rotate-180' : ''}`} />
        </button>
        {sidebarSections.meats && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {(data.filters?.meat_types || []).map(mt => (
              <button key={mt} onClick={() => { setActiveMeat(activeMeat === mt ? '' : mt); setPage(1); }}
                className={`px-3 py-1.5 rounded-full text-[12px] font-semibold transition-all border ${activeMeat === mt ? 'text-white border-amber-500 bg-amber-500' : 'border-amber-200 text-amber-700 hover:bg-amber-50'}`}
                data-testid={`filter-meat-${mt}`}>
                {filterLabels.meat_types[mt] || mt}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-slate-100 my-3" />

      {/* Dish types */}
      <div className="mb-4">
        <button onClick={() => setSidebarSections(s => ({ ...s, dishes: !s.dishes }))}
          className="flex items-center justify-between w-full py-2 text-left" data-testid="sidebar-toggle-dishes">
          <span className="text-[13px] font-bold uppercase tracking-wider text-slate-700">Type de plat</span>
          <CaretDown size={14} className={`text-slate-400 transition-transform ${sidebarSections.dishes ? 'rotate-180' : ''}`} />
        </button>
        {sidebarSections.dishes && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {(data.filters?.dish_types || []).map(dt => (
              <button key={dt} onClick={() => { setActiveDish(activeDish === dt ? '' : dt); setPage(1); }}
                className={`px-3 py-1.5 rounded-full text-[12px] font-semibold transition-all border ${activeDish === dt ? 'text-white border-emerald-600 bg-emerald-600' : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'}`}
                data-testid={`filter-dish-${dt}`}>
                {filterLabels.dish_types[dt] || dt}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  /* ═══════ RECIPE CARD ═══════ */
  const RecipeCard = ({ post, i }) => (
    <Link to={getLink(`/blog/${post.slug}`)}
      className="group block rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300 border border-slate-100 bg-white"
      data-testid={`blog-card-${i}`}>
      <div className="relative aspect-[16/9] overflow-hidden bg-slate-100">
        {post.featured_image && (
          <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute top-3 left-3">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold text-white backdrop-blur-sm"
            style={{ backgroundColor: colors.primary + 'dd' }}>
            <Knife size={12} weight="fill" /> Recette
          </span>
        </div>
        <div className="absolute bottom-3 left-3 right-3">
          <h2 className="text-[17px] font-bold text-white line-clamp-2 drop-shadow-md leading-snug">{post.title}</h2>
        </div>
      </div>
      <div className="p-4">
        <div className="flex flex-wrap gap-1.5 mb-3">
          {post.meat_types?.slice(0, 3).map(mt => (
            <span key={mt} className="px-2 py-0.5 rounded-full text-[10px] font-bold border border-amber-200 text-amber-700 bg-amber-50">
              {filterLabels.meat_types[mt] || mt}
            </span>
          ))}
          {post.dish_types?.slice(0, 2).map(dt => (
            <span key={dt} className="px-2 py-0.5 rounded-full text-[10px] font-bold border border-emerald-200 text-emerald-600 bg-emerald-50">
              {filterLabels.dish_types[dt] || dt}
            </span>
          ))}
        </div>
        <p className="text-[13px] text-slate-500 line-clamp-2 leading-relaxed mb-3">{post.excerpt}</p>
        <div className="flex items-center justify-between pt-2 border-t border-slate-50">
          <div className="flex items-center gap-3 text-[11px] text-slate-400">
            <span className="flex items-center gap-1"><Clock size={12} /> {new Date(post.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
            <span className="flex items-center gap-1"><Eye size={12} /> {post.views}</span>
          </div>
          <span className="text-[11px] font-bold group-hover:underline" style={{ color: colors.primary }}>Voir la recette</span>
        </div>
      </div>
    </Link>
  );

  /* ═══════ CONSEIL CARD ═══════ */
  const ConseilCard = ({ post, i }) => (
    <Link to={getLink(`/blog/${post.slug}`)}
      className="group flex gap-4 p-4 rounded-xl hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-100"
      data-testid={`blog-card-${i}`}>
      <div className="w-[120px] h-[90px] md:w-[160px] md:h-[110px] flex-shrink-0 rounded-lg overflow-hidden bg-slate-100">
        {post.featured_image && (
          <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        )}
      </div>
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
            style={{ backgroundColor: colors.primary + '15', color: colors.primary }}>
            {catIcons[post.category] || <BookOpenText size={11} />}
            {filterLabels.categories[post.category] || post.category}
          </span>
          {post.meat_types?.slice(0, 1).map(mt => (
            <span key={mt} className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-500">
              {filterLabels.meat_types[mt] || mt}
            </span>
          ))}
        </div>
        <h2 className="text-[15px] font-bold text-slate-800 group-hover:text-slate-600 transition-colors line-clamp-2 mb-1 leading-snug">{post.title}</h2>
        <p className="text-[12px] text-slate-400 line-clamp-1 leading-relaxed hidden sm:block">{post.excerpt}</p>
        <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-400">
          <span className="flex items-center gap-1"><Clock size={11} /> {new Date(post.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
          <span className="flex items-center gap-1"><Eye size={11} /> {post.views} vues</span>
        </div>
      </div>
    </Link>
  );

  return (
    <div className="min-h-screen bg-[#fafafa] flex flex-col overflow-x-hidden" style={{ fontFamily }} data-testid="blog-page">
      <link href="https://fonts.googleapis.com/css2?family=Jost:wght@300;400;500;600;700;900&display=swap" rel="stylesheet" />

      {/* ── MAGERE HEADER (shared) ── */}
      <MagereHeader store={store} activePage="blog" standalone={standalone} />

      <div className="h-[50px]" />

      {/* ── HERO BANNER ── */}
      <div className="relative py-12 md:py-16" style={{ backgroundColor: '#1a1a1a' }}>
        <div className="max-w-[1200px] mx-auto px-6 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.3em] mb-3 block" style={{ color: colors.primary }}>Blog & conseils</span>
          <h1 className="text-3xl md:text-5xl font-black text-white mb-3" style={{ letterSpacing: '-0.02em' }} data-testid="blog-title">
            Nos conseils
          </h1>
          <p className="text-white/50 text-sm md:text-base max-w-[550px] mx-auto">
            Recettes, astuces de cuisson, guides d'achat : profitez de notre expertise pour sublimer vos viandes
          </p>
        </div>
      </div>

      {/* ── MAIN CONTENT (sidebar + grid) ── */}
      <main className="flex-1 py-8 md:py-10">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <div className="flex gap-8">

            {/* ── LEFT SIDEBAR (desktop) ── */}
            <aside className="hidden lg:block w-[250px] flex-shrink-0">
              <div className="sticky top-[70px]">
                {/* Search */}
                <div className="relative mb-5">
                  <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                    placeholder="Rechercher..."
                    className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-white border border-slate-200 text-[13px] text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-slate-300 shadow-sm"
                    data-testid="blog-search" />
                </div>
                <SidebarFilter />
              </div>
            </aside>

            {/* ── CONTENT ── */}
            <div className="flex-1 min-w-0">
              {/* Mobile filter bar */}
              <div className="flex lg:hidden items-center gap-3 mb-5">
                <div className="relative flex-1">
                  <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                    placeholder="Rechercher..."
                    className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-white border border-slate-200 text-[13px] text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-slate-300 shadow-sm"
                    data-testid="blog-search-mobile" />
                </div>
                <button onClick={() => setMobileFilterOpen(!mobileFilterOpen)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-[13px] font-semibold text-slate-600 shadow-sm hover:bg-slate-50"
                  data-testid="mobile-filter-toggle">
                  <FunnelSimple size={16} /> Filtres {hasFilters && <span className="w-[6px] h-[6px] rounded-full" style={{ backgroundColor: colors.primary }} />}
                </button>
              </div>
              {mobileFilterOpen && (
                <div className="lg:hidden mb-6 p-4 rounded-xl bg-white border border-slate-200 shadow-sm">
                  <SidebarFilter />
                </div>
              )}

              {loading ? (
                <div className="space-y-6">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="animate-pulse flex gap-4 p-4">
                      <div className="w-[160px] h-[110px] bg-slate-100 rounded-lg flex-shrink-0" />
                      <div className="flex-1 space-y-2 py-2">
                        <div className="h-3 bg-slate-100 rounded w-1/4" />
                        <div className="h-5 bg-slate-100 rounded w-3/4" />
                        <div className="h-3 bg-slate-100 rounded w-full" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredPosts.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-2xl border border-slate-100">
                  <p className="text-lg text-slate-400 mb-2">Aucun article trouve</p>
                  {hasFilters && <button onClick={resetFilters} className="text-sm underline font-semibold" style={{ color: colors.primary }}>Effacer les filtres</button>}
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-5">
                    <p className="text-[13px] text-slate-500 font-medium">{data.total} article{data.total > 1 ? 's' : ''}</p>
                  </div>

                  {/* Recipe cards grid */}
                  {filteredPosts.some(p => isRecipe(p)) && (
                    <div className="mb-8">
                      <div className="flex items-center gap-2 mb-4">
                        <Knife size={18} weight="fill" style={{ color: colors.primary }} />
                        <h2 className="text-[16px] font-bold text-slate-800">Recettes</h2>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="recipe-grid">
                        {filteredPosts.filter(p => isRecipe(p)).map((post, i) => (
                          <RecipeCard key={post.id} post={post} i={i} />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Conseil/Other cards list */}
                  {filteredPosts.some(p => !isRecipe(p)) && (
                    <div>
                      {filteredPosts.some(p => isRecipe(p)) && (
                        <div className="flex items-center gap-2 mb-4">
                          <BookOpenText size={18} weight="fill" style={{ color: colors.primary }} />
                          <h2 className="text-[16px] font-bold text-slate-800">Conseils & Guides</h2>
                        </div>
                      )}
                      <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-50" data-testid="conseil-list">
                        {filteredPosts.filter(p => !isRecipe(p)).map((post, i) => (
                          <ConseilCard key={post.id} post={post} i={i + 100} />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Pagination */}
                  {data.pages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-10" data-testid="blog-pagination">
                      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                        className="p-2 rounded-lg border border-slate-200 disabled:opacity-30 hover:bg-slate-50 bg-white">
                        <ArrowLeft size={16} />
                      </button>
                      {[...Array(data.pages)].map((_, i) => (
                        <button key={i} onClick={() => setPage(i + 1)}
                          className={`w-9 h-9 rounded-lg text-[13px] font-semibold ${page === i + 1 ? 'text-white shadow-sm' : 'text-slate-600 hover:bg-white bg-transparent'}`}
                          style={page === i + 1 ? { backgroundColor: colors.primary } : {}}>
                          {i + 1}
                        </button>
                      ))}
                      <button onClick={() => setPage(p => Math.min(data.pages, p + 1))} disabled={page >= data.pages}
                        className="p-2 rounded-lg border border-slate-200 disabled:opacity-30 hover:bg-slate-50 bg-white">
                        <ArrowRight size={16} />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* ── MAGERE FOOTER (shared) ── */}
      <MagereFooter store={store} pages={pages} categories={categories} standalone={standalone} />
    </div>
  );
};

export default StoreBlogPage;
